import React from "react";
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export interface SelectOption {
  value: string;
  label: string;
}

const YES_NO_OPTIONS: SelectOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

interface BaseProps {
  label: string;
  options?: SelectOption[];
  helperText?: string;
  name?: string;
  onBlur?: React.FocusEventHandler;
  disabled?: boolean;
  placeholder?: string;
}

interface SingleSelectProps extends BaseProps {
  multiple?: false;
  // Widened beyond `string`: several callers hydrate this straight from API
  // responses where an unanswered field comes back as `null`/`undefined`.
  value: string | null | undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

interface MultiSelectProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type LabeledSelectProps = SingleSelectProps | MultiSelectProps;

export const LabeledSelect: React.FC<LabeledSelectProps> = ({
  label,
  value,
  onChange,
  options = YES_NO_OPTIONS,
  helperText,
  name,
  onBlur,
  multiple,
  disabled,
  placeholder,
}) => {
  const multiValue = Array.isArray(value) ? (value as string[]) : [];
  const multiOnChange = onChange as (value: string[]) => void;
  const selectId = `labeled-select-${(name || label).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`;
  const labelId = `${selectId}-label`;

  return (
    <Grid item xs={12}>
      <Stack gap={1}>
        {multiple ? (
          <FormControl fullWidth>
            <InputLabel id={labelId} shrink={!!placeholder || multiValue.length > 0}>{label}</InputLabel>
            <Select
              id={selectId}
              labelId={labelId}
              multiple
              value={multiValue}
              label={label}
              name={name}
              onBlur={onBlur}
              disabled={disabled}
              displayEmpty={!!placeholder}
              onChange={(e) => multiOnChange(e.target.value as string[])}
              renderValue={(selected) =>
                (selected as string[]).length === 0 && placeholder
                  ? placeholder
                  : (selected as string[])
                      .map((val) => options.find((o) => o.value === val)?.label ?? val)
                      .join(", ")
              }
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={multiValue.includes(option.value)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          // Normalize null/undefined to "" so MUI's Select always receives a
          // value that matches one of its <MenuItem>s (see the "" item below).
          <TextField
            label={label}
            select
            value={value ?? ""}
            onChange={onChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
            fullWidth
            name={name}
            onBlur={onBlur}
            disabled={disabled}
            SelectProps={placeholder ? { displayEmpty: true } : undefined}
          >
            <MenuItem value="" disabled sx={placeholder ? undefined : { display: "none" }}>
              {placeholder ?? ""}
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {helperText && (
          <Typography
            variant="inputLabel"
            sx={{ color: "var(--grey-600, #798397)", fontWeight: 400 }}
          >
            {helperText}
          </Typography>
        )}
      </Stack>
    </Grid>
  );
};

export default LabeledSelect;
